import api from '../../../common/api.js'
import createCSVFile from '../../../common/createCSVFile.js'
import {getNounsFromValue} from '../../../common/tokenizeOptions.js'
import {
  isEmptyValue,
  isNonZeroPositiveNumeric,
  isNumeric,
  isPresent,
} from '../../../common/utils.js'
import {hasProductFinancialsPermissionSelector} from '../../../data/me.js'
import {useSelector} from '../../../store.js'
import {
  columnOptionsSelector,
  columnOptionsToHeaders,
  processFiles,
  simpleColumnOptionsToRowArray,
} from '../dataImportFunctions.js'
import Header from '../Header.js'

const TYPE = 'create_products'
export const COLUMN_OPTIONS = [
  {
    value: 'sku',
    display: 'SKU',
    required: true,
    columnMap: /sku/i,
    error(state, {value}) {
      if (!isPresent(value)) {
        return 'SKU is required'
      }
    },
  },
  {
    value: 'name',
    display: 'Description',
    required: true,
    columnMap: /^description|name$/i,
    error(state, {value}) {
      if (!isPresent(value)) {
        return 'Name is required'
      }
    },
  },
  {
    value: 'price',
    display: 'Price',
    onlyIf: hasProductFinancialsPermissionSelector,
    columnMap: /price/i,
    error(state, {value}) {
      if (!isNumeric(value) && !isEmptyValue(value)) {
        return 'Price must be a number or empty'
      }
    },
  },
  {
    value: 'cost',
    display: 'Weighted Average Unit Cost',
    onlyIf: hasProductFinancialsPermissionSelector,
    columnMap: /^(weighted.)?(average.)?(unit.)?cost$/i,
    error(state, {value}) {
      if (!isNumeric(value) && !isEmptyValue(value)) {
        return 'Cost must be a number or empty'
      }
    },
  },
  {
    value: 'weight',
    display: 'Weight (lbs)',
    columnMap: /^weight( \(lbs\))?$/i,
    error(state, {value}) {
      if (!isNumeric(value) && !isEmptyValue(value)) {
        return 'Weight must be a number or empty'
      }
    },
  },
  {value: 'category', display: 'Category', columnMap: /category/i},
  {
    value: 'length',
    display: 'Length (in)',
    columnMap: /^length( \(in\))?$/i,
    error(state, {value}) {
      if (!isNonZeroPositiveNumeric(value) && !isEmptyValue(value)) {
        return 'Length must be a positive number or empty'
      }
    },
  },
  {
    value: 'width',
    display: 'Width (in)',
    columnMap: /^width( \(in\))?$/i,
    error(state, {value}) {
      if (!isNonZeroPositiveNumeric(value) && !isEmptyValue(value)) {
        return 'Width must be a positive number or empty'
      }
    },
  },
  {
    value: 'height',
    display: 'Height (in)',
    columnMap: /^height( \(in\))?$/i,
    error(state, {value}) {
      if (!isNonZeroPositiveNumeric(value) && !isEmptyValue(value)) {
        return 'Height must be a positive number or empty'
      }
    },
  },
  {value: 'upc', display: 'UPC', columnMap: /upc/i},
  {value: 'asin', display: 'ASIN', columnMap: /asin/i},
  {
    value: 'hs_code',
    display: 'Harmonized Code',
    columnMap: /hs_code|harmonized.code/i,
  },
  {
    value: 'country_of_origin',
    display: 'Country of Origin',
    columnMap: /^country(.of.origin)?$/i,
  },
  {
    value: 'declared_value',
    display: 'Declared Value',
    onlyIf: hasProductFinancialsPermissionSelector,
    columnMap: /^declared.value?$/i,
  },
  {
    value: 'customs_description',
    display: 'Customs Description',
    columnMap: /^customs.description$/i,
  },
  {
    value: 'internal_notes',
    display: 'Internal Notes',
    columnMap: /^internal.note(s)?/i,
  },
  {
    value: 'has_serial_numbers',
    display: 'Has Serial Numbers',
    columnMap: /serial.number(s)?/i,
  },
  {
    value: 'is_digital',
    display: 'Is Digital',
    columnMap: /^(is.)?digital/i,
  },
  {
    value: 'mo_internal_notes',
    display: 'MFG Internal Notes',
    columnMap: /^(mo|mfg).internal.note(s)?/i,
  },
  {
    value: 'mo_manufacturer_notes',
    display: 'MFG Manufacturer Notes',
    columnMap: /^(mo|mfg).manufacturer.note(s)?/i,
  },
  {
    value: 'needs_order_line_labels',
    display: 'Needs Order Line Labels',
    columnMap: /^needs.order.line.labels?/i,
  },
  {
    value: 'm_pid',
    display: 'EU M-PID',
    columnMap: /^(eu.)?m.pid?/i,
  },
  {
    value: 'ns_pid',
    display: 'EU NS-PID',
    columnMap: /^(eu.)?ns.pid?/i,
  },
  {
    value: 's_pid',
    display: 'EU S-PID',
    columnMap: /^(eu.)?s.pid?/i,
  },
]

async function createExampleFile(columnOptions) {
  const {json: data} = await api.get('/product/', {limit: 5})

  const headers = columnOptionsToHeaders(columnOptions)

  const rows = simpleColumnOptionsToRowArray(columnOptions, data.product)

  createCSVFile(headers, rows, 'Create_Product-example.csv')
}

function ProductCreateImport({importOptions}) {
  const columnOptions = useSelector((state) =>
    columnOptionsSelector(state, {columnOptions: COLUMN_OPTIONS}),
  )

  return (
    <div>
      <Header
        importOptions={importOptions}
        processFiles={(files) => processFiles(columnOptions, files)}
        createExampleFile={() => createExampleFile(columnOptions)}
        columnOptions={columnOptions}
        type={TYPE}
      />
    </div>
  )
}

const option = {
  value: TYPE,
  display: 'Create Products',
  category: 'products',
  Component: ProductCreateImport,
}
option.nouns = getNounsFromValue(
  `${option.value} ${option.display} ${option.category}`,
)
ProductCreateImport.option = option

export default ProductCreateImport
